/**
 * useModuleSidecar — resolve a module's sidecar.
 *
 * Lookup order:
 *   1. `BUILTIN_MODULE_SIDECARS` — compiled-in calibration data, available
 *      synchronously with no HTTP fetch. This is the default path for every
 *      built-in module type.
 *   2. `<assetsBaseUrl>/<id>.meta.json` — only consulted when there is no
 *      built-in entry for the type (consumer registered a custom module via
 *      `assetManifest`, or hot-reloads sidecars during development).
 *   3. Identity correction — when both fail.
 *
 * Live edits via `<XtsViewer3D sidecarOverrides={...}>` are applied at the
 * call site (not here), so calibration overrides keep working unchanged.
 */

import { useEffect, useState } from 'react';
import {
  SidecarLoader,
  type ModuleSidecar,
  type OriginCorrection,
  DEFAULT_ORIGIN_CORRECTION,
  moduleSidecarFilename,
} from '../../assets/SidecarLoader.js';
import { BUILTIN_MODULE_SIDECARS } from '../../assets/builtinSidecars.js';
import { useSidecarSource } from '../../assets/SidecarSourceContext.js';
import { composeAssetUrl } from '../../assets/AssetLoader.js';
import type { ModuleType3D } from '../../types.js';

const SHARED = new SidecarLoader();

export function useModuleSidecar(
  moduleType: ModuleType3D,
  assetsBaseUrl: string,
): { sidecar: ModuleSidecar | undefined; correction: OriginCorrection } {
  const { useBuiltins } = useSidecarSource();
  const builtin = useBuiltins ? BUILTIN_MODULE_SIDECARS[moduleType] : undefined;
  const [sidecar, setSidecar] = useState<ModuleSidecar | undefined>(builtin);

  useEffect(() => {
    if (builtin) {
      setSidecar(builtin);
      return;
    }
    let alive = true;
    const url = composeAssetUrl(
      assetsBaseUrl,
      moduleSidecarFilename(moduleType),
    );
    SHARED.fetchModule(url)
      .then((s) => {
        if (alive) setSidecar(s);
      })
      .catch(() => {
        if (alive) setSidecar(undefined);
      });
    return () => {
      alive = false;
    };
  }, [moduleType, assetsBaseUrl, builtin]);

  return {
    sidecar,
    correction: sidecar?.originCorrection ?? DEFAULT_ORIGIN_CORRECTION,
  };
}
